import { useState, useRef, useEffect } from "react";
import api, { fileUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Crop, Scissors } from "lucide-react";

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const HANDLES = ["nw", "ne", "sw", "se"];

export const CropDialog = ({ src, open, onClose, onCropped }) => {
  const [box, setBox] = useState({ x: 0.1, y: 0.1, w: 0.8, h: 0.8 });
  const [busy, setBusy] = useState(false);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const wrap = useRef(null);
  const drag = useRef(null);
  useEffect(() => { if (open) setBox({ x: 0.1, y: 0.1, w: 0.8, h: 0.8 }); }, [open, src]);

  const rel = (e) => {
    const r = wrap.current.getBoundingClientRect();
    return { x: clamp((e.clientX - r.left) / r.width, 0, 1), y: clamp((e.clientY - r.top) / r.height, 0, 1) };
  };
  const down = (e, mode) => {
    e.preventDefault(); e.stopPropagation();
    drag.current = { mode, start: rel(e), orig: { ...box } };
  };
  useEffect(() => {
    const move = (e) => {
      if (!drag.current || !wrap.current) return;
      const { mode, start, orig } = drag.current;
      const p = rel(e), dx = p.x - start.x, dy = p.y - start.y;
      if (mode === "move") {
        setBox({ ...orig, x: clamp(orig.x + dx, 0, 1 - orig.w), y: clamp(orig.y + dy, 0, 1 - orig.h) });
        return;
      }
      let x1 = orig.x, y1 = orig.y, x2 = orig.x + orig.w, y2 = orig.y + orig.h;
      if (mode.includes("w")) x1 = clamp(orig.x + dx, 0, x2 - 0.02);
      if (mode.includes("e")) x2 = clamp(orig.x + orig.w + dx, x1 + 0.02, 1);
      if (mode.includes("n")) y1 = clamp(orig.y + dy, 0, y2 - 0.02);
      if (mode.includes("s")) y2 = clamp(orig.y + orig.h + dy, y1 + 0.02, 1);
      setBox({ x: x1, y: y1, w: x2 - x1, h: y2 - y1 });
    };
    const up = () => { drag.current = null; };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, []);

  const apply = async (autoTrim) => {
    setBusy(true);
    const tid = toast.loading(autoTrim ? "Trimming transparent edges…" : "Cropping…");
    try {
      const { data } = await api.post("/crop-image", { url: src, ...box, auto_trim: autoTrim });
      const url = fileUrl(data.url);
      const im = new Image();
      im.onload = () => { onCropped(url, im.naturalWidth, im.naturalHeight); toast.success(autoTrim ? "Transparent edges trimmed" : "Image cropped", { id: tid }); onClose(); };
      im.onerror = () => { onCropped(url); toast.success("Image cropped", { id: tid }); onClose(); };
      im.src = url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Crop failed", { id: tid });
    } finally { setBusy(false); }
  };

  const pct = (v) => `${v * 100}%`;
  const outW = imgSize.w ? Math.round(box.w * imgSize.w) : 0, outH = imgSize.h ? Math.round(box.h * imgSize.h) : 0;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#12141a] border-zinc-800 text-zinc-100 max-w-2xl" data-testid="crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Crop size={18} className="text-[#22c3d6]" />Crop image</DialogTitle>
          <DialogDescription className="text-sm text-zinc-400">Drag the box or its corners. The cropped copy replaces this design on your sheet; the original stays untouched.</DialogDescription>
        </DialogHeader>
        <div className="flex justify-center">
          <div ref={wrap} className="relative inline-block checker rounded-lg overflow-hidden select-none border border-zinc-800" style={{ maxHeight: "55vh" }} data-testid="crop-stage">
            <img src={fileUrl(src)} alt="" draggable={false} onLoad={(e) => setImgSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })} className="block max-h-[55vh] max-w-full pointer-events-none" />
            <div className="absolute inset-0 pointer-events-none" style={{ background: `linear-gradient(rgba(0,0,0,.55),rgba(0,0,0,.55))`, clipPath: `polygon(0 0,100% 0,100% 100%,0 100%,0 ${pct(box.y)},${pct(box.x)} ${pct(box.y)},${pct(box.x)} ${pct(box.y + box.h)},${pct(box.x + box.w)} ${pct(box.y + box.h)},${pct(box.x + box.w)} ${pct(box.y)},0 ${pct(box.y)})` }} />
            <div onMouseDown={(e) => down(e, "move")} data-testid="crop-box" className="absolute border-2 border-[#22c3d6] cursor-move" style={{ left: pct(box.x), top: pct(box.y), width: pct(box.w), height: pct(box.h) }}>
              {HANDLES.map((h) => (
                <div key={h} onMouseDown={(e) => down(e, h)} data-testid={`crop-handle-${h}`} className="absolute w-3.5 h-3.5 bg-[#22c3d6] border-2 border-white rounded-sm"
                  style={{ top: h.includes("n") ? -7 : "auto", bottom: h.includes("s") ? -7 : "auto", left: h.includes("w") ? -7 : "auto", right: h.includes("e") ? -7 : "auto", cursor: h === "nw" || h === "se" ? "nwse-resize" : "nesw-resize" }} />
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
          <span className="text-xs text-zinc-400 font-mono-p" data-testid="crop-size">{outW && outH ? `${outW} × ${outH} px` : ""}</span>
          <div className="flex gap-2">
            <Button variant="outline" disabled={busy} onClick={() => apply(true)} data-testid="crop-auto-trim" className="border-[#10b981]/40 bg-[#10b981]/10 text-[#10b981] hover:bg-[#10b981]/20 rounded-full"><Scissors size={14} className="mr-1" />Auto-trim transparent edges</Button>
            <Button variant="outline" onClick={onClose} className="border-zinc-700 bg-transparent text-white hover:bg-zinc-800 rounded-full" data-testid="crop-cancel">Cancel</Button>
            <Button disabled={busy} onClick={() => apply(false)} data-testid="crop-apply" className="bg-[#22c3d6] hover:bg-[#1ba9ba] text-black rounded-full font-semibold"><Crop size={14} className="mr-1" />{busy ? "Working…" : "Apply crop"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
